import { Spin } from "antd";
import debounce from "lodash/debounce";
import React, { useEffect } from "react";
import { MOption } from "../module/private/shared/shared.model";
import CustomSelect, { CustomSelectProps } from "./CustomSelect";

export interface DebounceSelectProps<T extends MOption>
  extends Omit<CustomSelectProps<T>, "options"> {
  fetchOptions: (search: string) => Promise<T[]>;
  debounceTimeout?: number;
}

export function DebounceSelect<T extends MOption>({
  fetchOptions,
  debounceTimeout = 800,
  ...props
}: DebounceSelectProps<T>) {
  const [fetching, setFetching] = React.useState(false);
  const [options, setOptions] = React.useState<T[]>([]);
  const fetchRef = React.useRef(0);

  useEffect(() => {
    if (props.value) {
      let _options = [...options];
      _options.push(props.value);
      setOptions(_options);
    }

    return () => {
      setOptions([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.value]);

  const debounceFetcher = React.useMemo(() => {
    const loadOptions = (value: string) => {
      fetchRef.current += 1;
      const fetchId = fetchRef.current;
      setOptions([]);
      setFetching(true);

      fetchOptions(value).then((newOptions) => {
        if (fetchId !== fetchRef.current) {
          return;
        }

        setOptions(newOptions);
        setFetching(false);
      });
    };

    return debounce(loadOptions, debounceTimeout);
  }, [fetchOptions, debounceTimeout]);

  return (
    <CustomSelect<T>
      showSearch
      filterOption={false}
      onSearch={debounceFetcher}
      notFoundContent={fetching ? <Spin size="small" /> : null}
      {...props}
      options={options}
    ></CustomSelect>
  );
}
